/**
 * 조직별 브랜드 목록 조회 훅
 */
import { useState, useEffect, useCallback } from 'react';
import { organizationApi } from '@/lib/api/organizations';
import type { Brand, OrganizationSummary } from '@/lib/api/types';
import { logger } from '@/utils/logger';

interface UseOrganizationBrandsResult {
  brands: Brand[];
  organization: OrganizationSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useOrganizationBrands(orgParam: string | undefined): UseOrganizationBrandsResult {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [organization, setOrganization] = useState<OrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBrands = useCallback(async () => {
    if (!orgParam) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await organizationApi.getBrandsByOrg(orgParam);
      if (response.success) {
        setBrands(response.data.brands);
        setOrganization(response.data.organization);
      } else {
        setError('브랜드 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      logger.error('브랜드 목록 조회 오류:', err);
      setError(err instanceof Error ? err.message : '브랜드 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [orgParam]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  return {
    brands,
    organization,
    loading,
    error,
    refetch: fetchBrands,
  };
}
